/* eslint-disable react/prop-types */
/**
 * Bulk Import Audit Controls modal.
 *
 * Two actions:
 *   • Download Template — writes an EMPTY .xlsx with the exact same header
 *     row as `downloadTableView()` in AuditDetail so both formats stay
 *     synchronised. No sample data.
 *   • Upload File — reads .xlsx/.xls, validates headers + required fields +
 *     duplicate Control Refs against the audit's existing controls, then
 *     imports valid rows one-by-one through the existing manual
 *     add-control endpoint. Invalid rows are skipped with row-level
 *     messages.
 *
 * Business logic stays untouched — the modal calls the same
 * POST /api/audits/{audit_id}/controls endpoint the manual "Add Control"
 * dialog uses.
 */
import React, { useState, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, X, Loader2, RotateCcw } from 'lucide-react';

// Header row mirrors the "Download Excel" export in AuditDetail
// (`downloadTableView()`) so the same file can be reused both ways.
export const IMPORT_TEMPLATE_HEADERS = [
  '#', 'Control Ref', 'Control Name',
  'Frameworks', 'Categories', 'Technologies',
  'Type', 'Status', 'Automation Potential',
  'Evidence #', 'Evidence',
  'Policy', 'Automation Guidance', 'Description',
];

// Column widths preset (matches the export sheet)
const COLUMN_WIDTHS = {
  '#': 5, 'Control Ref': 16, 'Control Name': 40, 'Description': 60,
  'Frameworks': 20, 'Categories': 26, 'Technologies': 28,
  'Type': 10, 'Status': 14, 'Automation Potential': 20,
  'Automation Guidance': 55, 'Evidence #': 10, 'Evidence': 55, 'Policy': 30,
};

const VALID_TYPES = ['manual', 'automated'];
const VALID_STATUS = ['pending', 'in_progress', 'completed', 'not_applicable', 'passed', 'failed'];

// Map the template's dropdown labels (and common variants) to the canonical
// values the importer stores/accepts. Keeps imports error-free whether the
// user picked "Auto"/"In Review" from the dropdown or typed "automated" etc.
const TYPE_SYNONYMS = { manual: 'manual', auto: 'automated', automated: 'automated' };
const STATUS_SYNONYMS = {
  pending: 'pending', in_review: 'in_progress', in_progress: 'in_progress',
  review: 'in_progress', completed: 'completed', complete: 'completed',
  not_applicable: 'not_applicable', na: 'not_applicable', passed: 'passed', failed: 'failed',
};

const splitList = (v) => (v == null ? [] : String(v).split(/[;,\n]+/).map((s) => s.trim()).filter(Boolean));

// Normalize a status/type cell so users can enter "Manual", "manual" or "Passed" freely.
const norm = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, '_');

// Aggregate every Evidence <n> row belonging to the same *logical* control
// into a single import record. Rows are grouped by normalized Control Name
// + primary framework (the Control Ref is intentionally NOT used because
// exports sometimes explode a single logical control across multiple refs —
// e.g., row 85 and row 002 both being "Network Segmentation Configuration
// Checks for Amazon EC2" produced 2 rows before but should collapse to 1).
const _normNameKey = (name, framework) => {
  const n = String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
  const fw = String(framework || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
  return `${n}||${fw}`;
};

const aggregateEvidenceRows = (rows) => {
  const byKey = new Map();
  rows.forEach((r) => {
    const name = (r['Control Name'] || '').trim();
    if (!name) return; // skip nameless rows
    const framework = (splitList(r['Frameworks'])[0] || '').trim();
    const key = _normNameKey(name, framework);
    if (!byKey.has(key)) {
      byKey.set(key, { ...r, _evidence: [], _evidenceSet: new Set() });
    }
    const bucket = byKey.get(key);
    const ev = (r['Evidence'] || '').toString().trim();
    if (ev) {
      const nk = ev.toLowerCase().replace(/\s+/g, ' ');
      if (!bucket._evidenceSet.has(nk)) {
        bucket._evidenceSet.add(nk);
        bucket._evidence.push(ev);
      }
    }
  });
  return Array.from(byKey.values()).map((r) => {
    const { _evidenceSet, ...rest } = r;
    return { ...rest, Evidence: r._evidence.join('; ') };
  });
};

const validateRow = (row, opts) => {
  const { frameworks, categories, technologies } = opts;
  const errors = [];
  const name = (row['Control Name'] || '').trim();
  if (!name) errors.push('Control Name is required');
  // NOTE: We intentionally no longer flag duplicate Control Refs. Control
  // Ref is treated as informational (the export sometimes reuses it as an
  // evidence-record id). Deduplication is done by normalized Control Name
  // + framework, both on the client-side aggregation and on the server-
  // side bulk-import endpoint.
  const ref = (row['Control Ref'] || '').trim();
  const fw = splitList(row['Frameworks']);
  const cat = splitList(row['Categories']);
  const tech = splitList(row['Technologies']);
  const typ = TYPE_SYNONYMS[norm(row['Type'])] || norm(row['Type']);
  const stat = STATUS_SYNONYMS[norm(row['Status'])] || norm(row['Status']);
  if (typ && !VALID_TYPES.includes(typ)) errors.push(`Type must be Manual or Auto (got "${row['Type']}")`);
  if (stat && !VALID_STATUS.includes(stat)) errors.push(`Status must be one of: Pending, In Review, Completed, Not Applicable`);
  if (frameworks?.length && fw.some((f) => !frameworks.includes(f))) {
    errors.push(`Framework(s) not found in audit: ${fw.filter((f) => !frameworks.includes(f)).join(', ')}`);
  }
  if (categories?.length && cat.some((c) => !categories.includes(c))) {
    errors.push(`Category(ies) not found in audit: ${cat.filter((c) => !categories.includes(c)).join(', ')}`);
  }
  if (technologies?.length && tech.some((t) => !technologies.includes(t))) {
    errors.push(`Technology(ies) not found in audit: ${tech.filter((t) => !technologies.includes(t)).join(', ')}`);
  }
  return { errors, fw, cat, tech, typ, stat, name, ref };
};

const buildAddControlPayload = (v, row) => ({
  control_name: v.name,
  description: (row['Description'] || '').trim(),
  framework: v.fw[0] || '',
  category: v.cat[0] || '',
  type: v.typ || 'manual',
  evidence_required: splitList(row['Evidence']).slice(0, 8),
  automation_potential: norm(row['Automation Potential']) || undefined,
  automation_guidance: (row['Automation Guidance'] || '').trim() || undefined,
});

export default function BulkImportControlsDialog({
  open, onOpenChange, auditId, auditName, existingControls,
  auditFrameworks = [], auditCategories = [], auditTechnologies = [],
  onImportComplete,
}) {
  const [phase, setPhase] = useState('idle'); // idle | parsing | preview | importing | done
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]); // aggregated + validated
  const [globalError, setGlobalError] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0, imported: 0, skipped: 0 });
  const cancelRef = useRef(false);
  const fileRef = useRef(null);

  const existingRefs = useMemo(() => {
    // Kept for backward compat with any external callers; unused now that
    // duplicate detection is done server-side by normalized name.
    const s = new Set();
    (existingControls || []).forEach((c) => { if (c.control_ref) s.add(String(c.control_ref).trim().toLowerCase()); });
    return s;
  }, [existingControls]);

  const validationOpts = useMemo(() => ({
    existingRefs,
    frameworks: auditFrameworks,
    categories: auditCategories,
    technologies: auditTechnologies,
  }), [existingRefs, auditFrameworks, auditCategories, auditTechnologies]);

  const validCount = rows.filter((r) => r._errors.length === 0).length;
  const invalidCount = rows.length - validCount;

  const reset = () => {
    setPhase('idle'); setFileName(''); setRows([]);
    setGlobalError(''); setProgress({ current: 0, total: 0, imported: 0, skipped: 0 });
    cancelRef.current = false;
    if (fileRef.current) fileRef.current.value = '';
  };

  const closeAndReset = () => { reset(); onOpenChange(false); };

  const handleDownloadTemplate = async () => {
    // The template is generated server-side (openpyxl) so it can include Excel
    // dropdown / data-validation lists populated from THIS audit's frameworks,
    // technologies and categories. SheetJS (used for reading uploads) cannot
    // write data-validations.
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `/api/audits/${auditId}/controls/import-template`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const safe = (auditName || 'audit').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `${safe}_controls_import_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download template');
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setPhase('parsing');
    setFileName(file.name);
    setGlobalError('');
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      if (!wb.SheetNames.length) throw new Error('The uploaded file has no sheets.');
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      // Find the header row (first row containing "Control Name").
      const headerRowIdx = raw.findIndex((r) => Array.isArray(r) && r.some((c) => String(c || '').trim() === 'Control Name'));
      if (headerRowIdx < 0) throw new Error('Could not find the header row. Please use the provided template.');
      const headers = raw[headerRowIdx].map((h) => String(h || '').trim());
      const required = ['Control Name'];
      const missing = required.filter((h) => !headers.includes(h));
      if (missing.length) throw new Error(`Missing required column(s): ${missing.join(', ')}`);
      const objectRows = raw.slice(headerRowIdx + 1)
        .filter((r) => Array.isArray(r) && r.some((c) => String(c || '').trim() !== ''))
        .map((r) => Object.fromEntries(headers.map((h, i) => [h, r[i]])));
      if (!objectRows.length) throw new Error('The uploaded file contains no data rows.');
      const aggregated = aggregateEvidenceRows(objectRows);
      const validated = aggregated.map((row, i) => {
        const v = validateRow(row, validationOpts);
        return { ...row, _rowNum: i + 1, _errors: v.errors, _validated: v };
      });
      setRows(validated);
      setPhase('preview');
    } catch (e) {
      setGlobalError(e?.message || 'Could not parse file');
      setPhase('idle');
    }
  };

  const runImport = async () => {
    const valid = rows.filter((r) => r._errors.length === 0);
    if (!valid.length) return;
    cancelRef.current = false;
    setPhase('importing');
    setProgress({ current: 0, total: valid.length, imported: 0, skipped: 0 });
    const token = localStorage.getItem('token');

    // One atomic batch call to the server. The endpoint deduplicates by
    // normalized (Control Name + framework) — rows with the same logical
    // control merge their evidence into a single control, matching either
    // an existing DB control or an earlier row in this same batch.
    const items = valid.map((row) => buildAddControlPayload(row._validated, row));
    try {
      const res = await axios.post(
        `/api/audits/${auditId}/controls/bulk-import`,
        { items },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { created = 0, merged_into_existing: merged = 0, evidence_appended: evAdded = 0 } = res.data || {};
      setProgress({ current: valid.length, total: valid.length, imported: created, skipped: merged });
      setPhase('done');
      if (created > 0 || merged > 0) {
        const parts = [];
        if (created) parts.push(`${created} new control${created === 1 ? '' : 's'} created`);
        if (merged) parts.push(`${merged} row${merged === 1 ? '' : 's'} merged into existing controls`);
        if (evAdded) parts.push(`${evAdded} evidence line${evAdded === 1 ? '' : 's'} added`);
        toast.success(parts.join(' · '));
        if (onImportComplete) await onImportComplete();
      } else {
        toast.info('Nothing to import — every row already exists.');
        if (onImportComplete) await onImportComplete();
      }
    } catch (e) {
      setPhase('done');
      const detail = e?.response?.data?.detail || e?.message || 'Bulk import failed';
      toast.error(String(detail));
    }
  };

  const cancelImport = () => { cancelRef.current = true; };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeAndReset(); else onOpenChange(true); }}>
      <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[var(--tc-blue,#0F62FE)]" />
            Bulk Import Audit Controls
          </DialogTitle>
          <DialogDescription>
            Download the template, fill in your controls, and upload it back. Every row becomes a new control on this audit.
          </DialogDescription>
        </DialogHeader>

        {/* Idle → primary two actions */}
        {phase === 'idle' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-slate-200 rounded-lg p-4 flex flex-col gap-2">
                <div className="flex items-center gap-2 font-semibold text-slate-900"><Download className="w-4 h-4 text-slate-600" /> Download Template</div>
                <p className="text-xs text-slate-600">Excel template with dropdown lists for Type, Status, Frameworks, Technologies &amp; more — pick values instead of typing. Structure matches &quot;Download Excel&quot; exports.</p>
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="bulk-import-download-template" className="mt-auto">Download Template</Button>
              </div>
              <div className="border border-slate-200 rounded-lg p-4 flex flex-col gap-2">
                <div className="flex items-center gap-2 font-semibold text-slate-900"><Upload className="w-4 h-4 text-slate-600" /> Upload File</div>
                <p className="text-xs text-slate-600">Accepted formats: .xlsx or .xls. Blank rows are ignored automatically.</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={(e) => handleFile(e.target.files && e.target.files[0])}
                  className="text-xs mt-auto"
                  data-testid="bulk-import-file-input"
                />
              </div>
            </div>
            {globalError && (
              <div className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="bulk-import-global-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{globalError}</span>
              </div>
            )}
          </div>
        )}

        {/* Parsing → spinner */}
        {phase === 'parsing' && (
          <div className="py-8 flex flex-col items-center gap-2 text-slate-600 text-sm">
            <Loader2 className="w-6 h-6 animate-spin" /> Reading {fileName}…
          </div>
        )}

        {/* Preview */}
        {phase === 'preview' && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="border border-slate-200 rounded-lg p-3 text-center" data-testid="bulk-import-total">
                <div className="text-2xl font-semibold text-slate-900 tabular-nums">{rows.length}</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5">Controls Found</div>
              </div>
              <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3 text-center" data-testid="bulk-import-valid">
                <div className="text-2xl font-semibold text-emerald-700 tabular-nums">{validCount}</div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-700 mt-0.5">Valid</div>
              </div>
              <div className="border border-red-200 bg-red-50/40 rounded-lg p-3 text-center" data-testid="bulk-import-invalid">
                <div className="text-2xl font-semibold text-red-700 tabular-nums">{invalidCount}</div>
                <div className="text-[11px] uppercase tracking-wider text-red-700 mt-0.5">Invalid</div>
              </div>
            </div>
            <div className="max-h-[300px] overflow-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5 w-8">Row</th>
                    <th className="text-left px-2 py-1.5">Control</th>
                    <th className="text-left px-2 py-1.5 w-32">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r._rowNum} className="border-t border-slate-100 align-top" data-testid={`bulk-import-preview-row-${r._rowNum}`}>
                      <td className="px-2 py-1.5 tabular-nums text-slate-500">{r._rowNum}</td>
                      <td className="px-2 py-1.5 text-slate-800">
                        <div className="font-medium">{r._validated.name || <em className="text-slate-400">Missing name</em>}</div>
                        {r._errors.length > 0 && (
                          <ul className="text-red-600 text-[11px] mt-0.5 list-disc list-inside">
                            {r._errors.map((er, ei) => <li key={ei}>{er}</li>)}
                          </ul>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {r._errors.length === 0
                          ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Ready</span>
                          : <span className="inline-flex items-center gap-1 text-red-700"><AlertTriangle className="w-3 h-3" /> Invalid</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={reset} data-testid="bulk-import-restart">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Upload another file
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={closeAndReset}>Cancel</Button>
                <Button size="sm" onClick={runImport} disabled={validCount === 0} data-testid="bulk-import-import-btn">
                  Import {validCount} Control{validCount === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Importing */}
        {phase === 'importing' && (
          <div className="space-y-3 py-3">
            <div className="text-sm text-slate-700">
              Importing {progress.current} of {progress.total}… <span className="tabular-nums text-slate-500">({progress.imported} imported, {progress.skipped} skipped)</span>
            </div>
            <Progress value={progress.total ? (progress.current / progress.total) * 100 : 0} />
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={cancelImport} data-testid="bulk-import-cancel">
                <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Done */}
        {phase === 'done' && (
          <div className="space-y-4">
            <div className="border border-emerald-200 bg-emerald-50/60 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-900" data-testid="bulk-import-success-summary">
                  {progress.imported} control{progress.imported === 1 ? '' : 's'} imported successfully.
                </div>
                {progress.skipped > 0 && (
                  <div className="text-sm text-emerald-900/80 mt-0.5">
                    {progress.skipped} control{progress.skipped === 1 ? '' : 's'} {progress.skipped === 1 ? 'was' : 'were'} skipped because of validation errors.
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={reset} data-testid="bulk-import-again">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Import another file
              </Button>
              <Button size="sm" onClick={closeAndReset} data-testid="bulk-import-close">Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
